import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { WarningIcon, ArrowCounterClockwiseIcon } from '@phosphor-icons/react';
import WorkflowConstantsPanel from '../WorkflowConstantsPanel';
import { SpreadsheetViewers } from '../index';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import WorkflowStepsDisplay from '@/components/common/WorkflowStepsDisplay';
import { WorkflowGenerateWorkflowResponse } from 'src/api/spreadsheet/workflow';
import { WorkflowConstants } from '../../types/WorkflowConstants';

// Phase 3: Workflow Generation - Sheet selection is already complete at this point
interface PhaseThreeWorkflowProps {
  mappedData: unknown[][]; // Phase 2 output (input to this phase)
  transformedData: unknown[][]; // Phase 3 output (final)
  isLoadingMapped: boolean;
  isLoadingTransformed: boolean;
  selectedFiles: File[];
  transformError: string | null;
  isGeneratingWorkflow: boolean;
  workflowData: WorkflowGenerateWorkflowResponse | null;
  onMappedDataChange: (data: unknown[][]) => void;
  setTransformedData: (data: unknown[][]) => void;
  constants: WorkflowConstants | null;
  supplierName?: string;
  onConversationClear: () => void;
  // Workflow error handling
  workflowSuccess?: boolean;
  workflowError?: string;
  failedStepId?: string;
  // Currency conversion
  onApplyCurrencyConversion?: (rate: number) => void;
  conversionApplied?: boolean;
  // Constants editing (Phase 3 allows editing currency and date range)
  onConstantsChange?: (constants: WorkflowConstants) => void;
  // Unapplied changes tracking
  hasUnappliedConstantChanges?: boolean;
  hasUnappliedConversionRateChange?: boolean;
  onResetConstants?: () => void;
  onConversionRateChange?: (rate: number | undefined) => void;
  bannerError?: string | null;
}

const PhaseThreeWorkflow: React.FC<PhaseThreeWorkflowProps> = ({
  mappedData,
  transformedData,
  isLoadingMapped,
  isLoadingTransformed,
  selectedFiles,
  transformError,
  isGeneratingWorkflow,
  workflowData,
  onMappedDataChange,
  setTransformedData,
  constants,
  supplierName,
  onConversationClear,
  workflowError,
  failedStepId,
  onApplyCurrencyConversion,
  conversionApplied,
  onConstantsChange,
  hasUnappliedConstantChanges,
  hasUnappliedConversionRateChange,
  onResetConstants,
  onConversionRateChange,
  bannerError,
}) => {
  const filename = useMemo(
    () => selectedFiles[0]?.name || 'spreadsheet',
    [selectedFiles],
  );

  const dataSection = useMemo(
    () => (
      <div className="mt-8">
        <SpreadsheetViewers
          title="Workflow Results"
          originalTitle="Mapped Data"
          transformedTitle="Transformed Data"
          originalData={mappedData}
          transformedData={transformedData}
          isLoadingOriginal={isLoadingMapped}
          isLoadingTransformed={isLoadingTransformed}
          transformError={transformError}
          transformationError={transformError} // Pass transformation error for display
          workbook={null} // No sheet selection in workflow phase
          selectedSheet={undefined} // No sheet selection in workflow phase
          onConversationClear={onConversationClear}
          onOriginalDataChange={onMappedDataChange}
          onTransformedDataChange={setTransformedData}
          filename={filename}
        />
      </div>
    ),
    [
      mappedData,
      transformedData,
      isLoadingMapped,
      isLoadingTransformed,
      transformError,
      onConversationClear,
      onMappedDataChange,
      setTransformedData,
      filename,
    ],
  );

  // Find all successfully executed split_row operations
  const splitRowSteps = useMemo(() => {
    if (!workflowData?.data?.workflowSteps) return [];
    return workflowData.data.workflowSteps.filter(
      (step) => step.type === 'split_row' && step.status === 'success',
    );
  }, [workflowData]);

  const hasAnyUnappliedChanges =
    hasUnappliedConstantChanges || hasUnappliedConversionRateChange;

  return (
    <div>
      <WorkflowConstantsPanel
        constants={constants}
        supplierName={supplierName}
        phase={3}
        disabled={isGeneratingWorkflow}
        onConstantsChange={onConstantsChange}
        onApplyCurrencyConversion={onApplyCurrencyConversion}
        conversionApplied={conversionApplied}
        showWorkflowCompleted={Boolean(workflowData)}
        onConversionRateChange={onConversionRateChange}
      />
      {bannerError && (
        <div className="mb-4">
          <div className="rounded-lg border border-red-400 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.054 0 1.662-1.14 1.118-2.054L13.118 4.946c-.526-.894-1.71-.894-2.236 0L4.944 17.946c-.544.914.064 2.054 1.118 2.054z"
              />
            </svg>
            <div>{bannerError}</div>
          </div>
        </div>
      )}

      {/* Unapplied Changes Warning */}
      {hasAnyUnappliedChanges && (
        <Card className="mb-6 border-red-500 bg-red-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <WarningIcon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  ⚠️ Unapplied Changes
                </h3>
                <div className="text-sm text-red-800 space-y-1">
                  {hasUnappliedConstantChanges && (
                    <p>
                      • You have modified the <strong>currency</strong> or{' '}
                      <strong>date range</strong>. These changes require
                      re-running the workflow to take effect.
                    </p>
                  )}
                  {hasUnappliedConversionRateChange && (
                    <p>
                      • You have changed the <strong>conversion rate</strong>{' '}
                      but haven&apos;t applied it yet.
                    </p>
                  )}
                </div>
                <p className="text-sm text-red-800 mt-3 font-medium">
                  You cannot submit until you either apply these changes or
                  reset them.
                </p>
              </div>
              {onResetConstants && (
                <Button
                  onClick={onResetConstants}
                  variant="outline"
                  className="border-red-600 text-red-700 hover:bg-red-100"
                >
                  <ArrowCounterClockwiseIcon className="w-4 h-4 mr-2" />
                  Reset Changes
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <h2 className="text-xl font-semibold mb-6">
        Workflow Generation & Execution
      </h2>

      <p className="text-sm text-gray-600 mb-6">
        The goal of this step is to generate and execute a custom transformation
        to make the data in the correct ready format for submission. AI will
        analyze your data, figure out what csv transformations are needed, and
        then execute them.
      </p>

      {/* Workflow Steps Display */}
      <WorkflowStepsDisplay
        workflowSteps={workflowData?.data.workflowSteps}
        rawWorkflowSteps={workflowData?.data.rawWorkflowSteps}
        isGenerating={isGeneratingWorkflow}
        title="Workflow Steps"
        tooltip="These are the CSV operations the AI used to shape your data into the correct format. Each step represents a specific transformation applied to your spreadsheet data."
        phase="transformation"
        workflowError={workflowError}
        failedStepId={failedStepId}
      />

      {/* Row splitting banners - show one banner per split_row operation */}
      {splitRowSteps.map((step, index) => {
        const inputs = step.inputs as {
          targetColumn?: string;
          splitMap?: Record<string, string>;
        };
        const splitMapKeys = inputs.splitMap
          ? Object.keys(inputs.splitMap)
          : [];
        const targetColumn = inputs.targetColumn || 'values';
        const periodCount = splitMapKeys.length;
        const startDate = splitMapKeys[0];
        const endDate = splitMapKeys[splitMapKeys.length - 1];

        return (
          <div key={step.id || `split-row-${index}`} className="mb-8">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <p className="text-base text-blue-900 font-medium">
                      Row Distribution Applied
                    </p>
                    <p className="text-sm text-blue-800 mt-1">
                      Distributed <strong>{targetColumn}</strong> values evenly
                      across {periodCount} time period
                      {periodCount !== 1 ? 's' : ''}.
                    </p>
                    {startDate && endDate && (
                      <p className="text-sm text-blue-700 mt-1">
                        Date range: {startDate} → {endDate}
                      </p>
                    )}
                    {step.description && (
                      <p className="text-xs text-blue-600 mt-2 italic">
                        {step.description}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })}

      {/* Constants and currency conversion shown above via WorkflowConstantsPanel */}

      {/* Data Preview */}
      {isGeneratingWorkflow ? (
        <div className="mt-8">
          <Card>
            <CardContent>
              <LoadingSpinner
                text="Processing transformation results..."
                minHeight={200}
                size={48}
              />
              <p className="text-sm text-gray-600 mt-4 text-center">
                The transformation is still processing. Please wait for complete
                results.
              </p>
            </CardContent>
          </Card>
        </div>
      ) : (
        dataSection
      )}
    </div>
  );
};

export default PhaseThreeWorkflow;
